import animateScrollTo from 'animated-scroll-to';

/**
 * This project a JavaScript for smooth scrolling. The {@link header} changes it's visibilty based on the current scroll position. Smooth scrolling is cancelled, when the visibility of the header changes during the scroll event. We {@link lock} the state of the header, whenever we click an anchorlink and {@link unlock} it after a short {@link unlockDelay}.
 */
export const lockHeaderOnSmoothScroll = () => {
  const header: HTMLElement = document.querySelector('[is="basis-header"]');
  if (!header) return;

  const unlockDelay = 100;

  /** Maximum scroll duration in miliseconds (`1000` = 1s) */
  const maxDuration = 800;

  /** Unlock the header */
  const unlock = () => {
    header.removeAttribute('data-locked');
  };

  /** Set the header to locked mode. It will not change its current state on scroll. When it was hidden, it will remain hidden. When it was visible, it will remain visible. */
  const lock = () => {
    header.setAttribute('data-locked', 'true');
  };

  /**
   * Smooth Scrolling with „go to element if found in url” and „click on element” on elements with a certain {@link selector} within a {@link container} or the `document`
   * @param container Any parent container that contains links. Default is `document`. Use this only to apply the smooth scrolling behaviour on elements that were added to the DOM after the DOM was loaded.
   * @copyright Friendventure GmbH
   * @returns {void}
   * @example
   *   smoothScrolling() // on document
   *   smoothScrolling(document.querySelector('.foo')) // on target element
   */
  const smoothScrolling = (container: HTMLElement | Document = document) => {
    /** Hash like `'#my-anchor'` in current URL */
    const locationHash = window.location.hash;

    /** Selector for anchorlinks that should have the smooth scrolling behaviour */
    const selector = 'a[href*="#"]:not([href="#"]):not([href="#0"])';

    /** Element containing all anchor links with a {@link selector} */
    const wrapper = container ?? document;

    /** Anchor links that lead somewhere on the current page */
    const anchorLinks: HTMLAnchorElement[] = Array.from(
      wrapper.querySelectorAll(selector)
    );

    /**
     * Animate scroll to any `Element` and focus
     * @param id Any id
     */
    const scrollToAndFocusElement = (id: string) => {
      /** Extra space to be added to the top edge of the viewport when scrolling */
      let verticalOffset = 32;

      const targetElement = document.getElementById(id);
      if (!targetElement) return;

      /** Lock the header */
      lock();

      if (header.getAttribute('data-current-state') === 'visible') {
        verticalOffset += header.clientHeight;
      }

      animateScrollTo(targetElement, {
        maxDuration,
        verticalOffset: verticalOffset * -1
      }).then(() => {
        targetElement.focus();

        /** Unlock the header */
        setTimeout(() => {
          unlock();
        }, unlockDelay);
      });
    };

    /**
     * Add animated scrolling to page-internal anchor links
     */
    if (anchorLinks && anchorLinks.length) {
      anchorLinks.forEach((link) => {
        /**
         * On click callback
         * @param e Click event
         */
        const onClick = (e: MouseEvent) => {
          const linkUrl = new URL(link.href);
          const currentUrl = new URL(window.location.href);

          /** Only manipulate the scrolling behavior if it's a page internal link */
          if (
            decodeURIComponent(linkUrl.origin) +
              decodeURIComponent(linkUrl.pathname) !==
            decodeURIComponent(currentUrl.origin) +
              decodeURIComponent(currentUrl.pathname)
          ) {
            return;
          }

          e.preventDefault();

          const linkHash = decodeURIComponent(link.hash.substring(1));
          if (!linkHash) return;
          currentUrl.hash = linkHash;

          /** Change the hash in the current url {@link currentUrl} (also in the address bar of the browser) */
          window.history.pushState({}, '', currentUrl);

          /** Animated scrolling and focus */
          scrollToAndFocusElement(linkHash);
        };

        link.addEventListener('click', onClick);
      });
    }

    /**
     * Add animated scrolling to the anchor if URL contains a hash on page load
     */
    if (locationHash) {
      scrollToAndFocusElement(locationHash.substring(1));
    }
  };

  smoothScrolling();
};
